import type { ComponentProps } from "react";
import type { OrgStoryView } from "@/lib/orgStory";
import {
  buildCatalogIndex,
  normalizeToolStack,
  type CatalogEntry,
  type NormalizedStack,
} from "@/lib/toolStack";
import type ProductionMetrics from "@/components/organization/performance/ProductionMetrics";
import type AgentPerformance from "@/components/organization/performance/AgentPerformance";
import type MarketRankings from "@/components/organization/performance/MarketRankings";
import type GrowthTrajectory from "@/components/organization/performance/GrowthTrajectory";
import type MarketPresenceCard from "@/components/organization/performance/MarketPresence";

/**
 * Pure data shaping for the org story page (spec 2026-09-24 §5.1). Each builder
 * reproduces what OrganizationProfile.tsx's Overview and Performance tabs
 * compute today, reading the OrgStoryView, so the story shows exactly the same
 * figures.
 *
 * These builders ARE the per-block render gate, mirroring the page: a null or
 * empty block here means the page would not render that component either, and
 * `buildPerformance` returning `null` mirrors the page's own `hasPerfData`
 * check. `storySections` (in `@/lib/orgStory`) is a separate, coarser
 * decision — it decides only whether the whole Footprint section of the story
 * exists at all — and is unaffected by these builders.
 */

type Pd = Record<string, any>;

export function formatDollar(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === "") return "$0";
  const n = Number(v);
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

export function yearsInBusiness(foundedYear: number | null, now: Date = new Date()): number | null {
  if (foundedYear == null) return null;
  const years = now.getFullYear() - foundedYear;
  return years > 0 ? years : null;
}

export type QuickStatIcon = "building" | "users" | "trending" | "location" | "dollar" | "home";

export interface QuickStatSpec {
  label: string;
  value: string;
  icon?: QuickStatIcon;
  subtext?: string;
  /** The agent count is the opted-in verified count — render VerifiedAgentCountLabel. */
  verified?: boolean;
}

// Mirrors OrganizationProfile.tsx's canonicalLabelPattern exactly (Overview tab).
const CANONICAL_LABEL = /year|agent|transaction|volume|sale price/;

/**
 * Overview's "By The Numbers" stats: canonical metrics first (years in
 * business, active agents, total volume, transactions, average sale price),
 * then free-typed byTheNumbers entries whose label doesn't duplicate a
 * canonical one.
 */
export function buildQuickStats(view: OrgStoryView, now: Date = new Date()): QuickStatSpec[] {
  const pd = view.profileData as Pd;
  const stats: QuickStatSpec[] = [];

  const years = yearsInBusiness(view.identity.foundedYear, now);
  if (years) {
    stats.push({ label: "Years in business", value: String(years), icon: "building" });
  }

  // The page never omits the "verified" marker on this entry, even when
  // false — it is what distinguishes a self-reported figure from a
  // platform-verified one at the render layer.
  if (view.agentCount != null) {
    stats.push({
      label: view.agentCountVerified ? "Agents on Compaire" : "Active agents",
      value: view.agentCount.toLocaleString(),
      icon: "users",
      verified: view.agentCountVerified,
    });
  }

  const pm: Pd = (pd.performanceData as Pd)?.productionMetrics || {};
  if (pm.totalVolume) {
    stats.push({ label: "Total volume", value: formatDollar(pm.totalVolume), icon: "dollar" });
  }
  if (pm.totalTransactions) {
    stats.push({ label: "Transactions", value: Number(pm.totalTransactions).toLocaleString(), icon: "home" });
  }
  if (pm.avgSalePrice) {
    stats.push({ label: "Average sale price", value: formatDollar(pm.avgSalePrice), icon: "trending" });
  }

  const byTheNumbers: Pd[] = Array.isArray(pd.byTheNumbers) ? pd.byTheNumbers : [];
  const extra: QuickStatSpec[] = byTheNumbers
    .filter((m) => typeof m?.value === "string" && m.value.trim() && typeof m?.label === "string" && m.label.trim())
    .filter((m) => !CANONICAL_LABEL.test(m.label.toLowerCase()))
    .map((m) => (m.context ? { label: m.label, value: m.value, subtext: m.context } : { label: m.label, value: m.value }));

  return [...stats, ...extra];
}

/**
 * "How it runs" tools: the saved stack read through the one normalizer
 * (spec 2026-10-15 §4.2). The catalog resolves categories for legacy entries;
 * an empty catalog still works because new entries carry their category.
 */
export function buildToolStack(pd: Pd, catalog: CatalogEntry[]): NormalizedStack {
  return normalizeToolStack(pd.technologyTools, buildCatalogIndex(catalog));
}

export interface StoryPerformance {
  reportingYear: string;
  production: ComponentProps<typeof ProductionMetrics> | null;
  agents: ComponentProps<typeof AgentPerformance> | null;
  rankings: ComponentProps<typeof MarketRankings>["rankings"];
  growth: ComponentProps<typeof GrowthTrajectory>["milestones"];
  presence: ComponentProps<typeof MarketPresenceCard> | null;
}

/**
 * Performance tab: production metrics, agent outcomes, rankings, growth
 * timeline and market presence, shaped exactly as OrganizationProfile.tsx's
 * Performance tab computes them today.
 *
 * One page quirk is reproduced faithfully rather than "fixed" (per the plan's
 * instruction to follow the page): the page's own `hasPerfData` gate's
 * market-presence clause omits `listToSaleRatio` — it requires
 * `perfData.marketPresence && (marketShare || daysOnMarket || yearsStr ||
 * specializations…)` — while its separate `hasMarketPresence` (which gates
 * the MarketPresenceCard itself, once the tab is already open) includes
 * `listToSaleRatio` and does not require the `marketPresence` object to be
 * present at all (founded year alone can supply it). That asymmetry between
 * the two gates is reproduced here rather than reconciled: `listToSaleRatio`
 * alone in `marketPresence` cannot by itself open the tab, but it (and a
 * founded year with no `marketPresence` object at all) DOES surface in
 * `presence` once something else has opened the tab.
 */
export function buildPerformance(view: OrgStoryView, now: Date = new Date()): StoryPerformance | null {
  const pd = view.profileData as Pd;
  const perfSource = pd.performanceData as Pd | undefined;
  // Mirrors the page's own `const hasPerfData = perfData && (...)` — with no
  // performanceData object at all, the Performance tab renders its empty
  // state regardless of anything else on the profile (including a declared
  // agentCount).
  if (!perfSource) return null;

  const perf: Pd = perfSource;
  const pm: Pd = perf.productionMetrics || {};
  const qt: Pd[] = Array.isArray(perf.quarterlyTrend) ? perf.quarterlyTrend : [];
  const ap: Pd = perf.agentPerformance || {};
  const distRaw: Pd[] = Array.isArray(perf.incomeDistribution) ? perf.incomeDistribution : [];
  const marketPresencePresent = perf.marketPresence != null;
  const mkt: Pd = perf.marketPresence || {};
  const ranksRaw: Pd[] = Array.isArray(perf.rankings) ? perf.rankings : [];
  const timelineRaw: Pd[] = Array.isArray(perf.growthTimeline) ? perf.growthTimeline : [];

  const years = yearsInBusiness(view.identity.foundedYear, now);
  const yearsStr = years ? String(years) : undefined;

  const hasCompleteQuarter = qt.some((q) => q?.label && q?.volume);
  const rawAgentCount = Number(pd.agentCount);
  const hasRawAgentCount = Number.isFinite(rawAgentCount) && rawAgentCount > 0;

  // Mirrors the page's own gate clause exactly, including requiring the
  // `marketPresence` object to be present (not just its fields) — see the
  // header comment above on the listToSaleRatio/object-presence asymmetry.
  const hasMarketPresenceForGate =
    marketPresencePresent &&
    !!(mkt.marketShare || mkt.daysOnMarket || yearsStr || (Array.isArray(mkt.specializations) && mkt.specializations.length > 0));

  const hasPerfData = !!(
    pm.totalVolume ||
    pm.totalTransactions ||
    pm.avgSalePrice ||
    hasRawAgentCount ||
    ap.incomeGoalHitRate ||
    distRaw.length > 0 ||
    hasCompleteQuarter ||
    ranksRaw.length > 0 ||
    timelineRaw.length > 0 ||
    hasMarketPresenceForGate
  );

  if (!hasPerfData) return null;

  const volumeChange = pm.volumeGrowthYoY
    ? pm.volumeGrowthSign === "negative"
      ? -Number(pm.volumeGrowthYoY)
      : Number(pm.volumeGrowthYoY)
    : 0;
  const quarterly = qt
    .filter((q) => q?.label && q?.volume)
    .map((q) => ({ label: q.label, value: Math.round(Number(q.volume) / 1_000_000) }));
  const hasProduction = !!(pm.totalVolume || pm.totalTransactions || pm.avgSalePrice || pm.volumeGrowthYoY || hasCompleteQuarter);

  const distribution = distRaw
    .filter((d) => d?.range && d.percentage !== "" && d.percentage != null)
    .map((d) => ({ band: d.range, pct: Number(d.percentage) }));
  const activeAgents = view.agentCount != null ? view.agentCount.toLocaleString() : null;
  const pct = (v: unknown) => (v ? `${v}%` : null);
  const hasAgentOutcomes =
    distribution.length > 0 ||
    !!ap.incomeGoalHitRate ||
    !!activeAgents ||
    !!ap.retentionRate ||
    !!ap.avgTenure ||
    !!ap.newAgentSuccessRate;

  const rankings = ranksRaw
    .filter((r) => r?.rank && r?.category)
    .map((r) => ({ position: r.rank, category: r.category, location: r.region || "", source: r.source || undefined }));

  // Mirrors the page's own GrowthTrajectory gate exactly: the component
  // renders only when at least one milestone has a year AND (agents or
  // volume); once that passes, every row with a year is mapped in, including
  // ones missing agents/volume individually.
  const hasGrowth = timelineRaw.some((t) => t?.year && (t.agents || t.volume));
  const growth = hasGrowth
    ? timelineRaw
        .filter((t) => t?.year)
        .sort((a, b) => Number(a.year) - Number(b.year))
        .map((t) => ({ year: Number(t.year), agents: Number(t.agents || 0), volume: formatDollar(t.volume || "0") }))
    : [];

  const hasMarketPresence = !!(
    mkt.marketShare ||
    mkt.daysOnMarket ||
    mkt.listToSaleRatio ||
    yearsStr ||
    (Array.isArray(mkt.specializations) && mkt.specializations.length > 0)
  );

  const reportingYear = perf.reportingYear || String(now.getFullYear());

  return {
    reportingYear,
    production: hasProduction
      ? {
          annualVolume: pm.totalVolume ? formatDollar(pm.totalVolume) : null,
          transactions: pm.totalTransactions ? Number(pm.totalTransactions).toLocaleString() : null,
          avgSalePrice: pm.avgSalePrice ? formatDollar(pm.avgSalePrice) : null,
          yoyGrowth: volumeChange ? `${volumeChange > 0 ? "+" : ""}${volumeChange}%` : null,
          quarterly,
          reportingYear,
        }
      : null,
    agents: hasAgentOutcomes
      ? {
          activeAgents,
          activeAgentsVerified: view.agentCountVerified,
          retention: pct(ap.retentionRate),
          avgTenure: ap.avgTenure ? `${ap.avgTenure} yrs` : null,
          newAgentSuccess: pct(ap.newAgentSuccessRate),
          incomeGoalHitRate: pct(ap.incomeGoalHitRate),
          distribution,
        }
      : null,
    rankings,
    growth,
    presence: hasMarketPresence
      ? {
          marketShare: mkt.marketShare,
          daysOnMarket: mkt.daysOnMarket,
          listToSaleRatio: mkt.listToSaleRatio,
          yearsInBusiness: yearsStr,
          specializations: mkt.specializations,
        }
      : null,
  };
}
